import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { usePageCms } from "@/lib/usePageCms";
import { PageHero, SectionHeading } from "@/components/site/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ShieldCheck } from "lucide-react";

const PROVIDER_TYPES = [
  ["hotel", "Hotel"], ["resort", "Resort"], ["homestay", "Homestay"], ["villa", "Villa"],
  ["activity", "Activity / Experience Provider"], ["transport", "Transport Provider"],
  ["tour_guide", "Tour Guide"], ["other", "Other Tourism Service Provider"],
];
const ACCOMMODATION = ["hotel", "resort", "homestay", "villa"];
const INDIAN_MOBILE = (v) => /^[6-9]\d{9}$/.test((v || "").replace(/\D/g, "").replace(/^0091|^91|^0/, ""));
const HTTPS_OK = (v) => !v || (!/^(javascript|data|vbscript|file):/i.test(v.trim()) && /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}/i.test(v.trim()));

function Field({ label, required, children, error, testid, help }) {
  return (
    <div className="space-y-1.5">
      <Label className="font-accent text-xs font-semibold text-charcoal">{label}{required && <span className="text-saffron"> *</span>}</Label>
      {children}
      {help && <p className="text-[11px] text-muted-foreground">{help}</p>}
      {error && <p data-testid={testid ? `${testid}-error` : undefined} className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export default function ServiceProviderRegistration() {
  const navigate = useNavigate();
  const FB = {
    overline: "Work With Maha Sampada", title: "Register as a Service Provider",
    subtitle: "Hotels, resorts, homestays, villas, activity and transport providers and tour guides across Maharashtra can express interest in working with us.",
    image: "https://images.pexels.com/photos/37886183/pexels-photo-37886183.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
    seo_title: "Register as a Service Provider | Maha Sampada Maharashtra DMC",
    seo_description: "Maharashtra hotels, resorts, homestays, villas, activity and transport providers and tour guides can express interest in working with Maha Sampada.",
  };
  const { hero } = usePageCms("service-provider-registration", FB);
  useSeo({ title: hero.seo_title || FB.seo_title, description: hero.seo_description || FB.seo_description });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    provider_type: "", business_name: "", contact_person: "", mobile: "", whatsapp: "", email: "",
    city: "", district: "", state: "Maharashtra", services: "",
    website: "", maps_url: "", full_address: "", operating_areas: "", years_in_operation: "",
    gst: "", license_ref: "", message: "",
    property_category: "", num_rooms: "", room_categories: "", meal_plans: "",
    vehicle_types: "", fleet_size: "", transport_areas: "",
    activity_categories: "", activity_location: "", capacity: "", seasonality: "",
    consent: false, accuracy_confirmed: false,
  });
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const isAccommodation = ACCOMMODATION.includes(form.provider_type);
  const isTransport = form.provider_type === "transport";
  const isActivity = form.provider_type === "activity";

  const validate = () => {
    const e = {};
    if (!form.provider_type) e.provider_type = "Select a provider type";
    if (!form.business_name.trim()) e.business_name = "Business / property name is required";
    if (!form.contact_person.trim()) e.contact_person = "Contact person is required";
    if (!INDIAN_MOBILE(form.mobile)) e.mobile = "Enter a valid Indian mobile number";
    if (form.whatsapp && !INDIAN_MOBILE(form.whatsapp)) e.whatsapp = "Enter a valid WhatsApp number or leave it blank";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = "Enter a valid email address";
    if (!form.city.trim()) e.city = "City / town is required";
    if (!form.district.trim()) e.district = "District is required";
    if (!form.state.trim()) e.state = "State is required";
    if (!form.services.trim()) e.services = "Please describe the services you offer";
    if (!HTTPS_OK(form.website)) e.website = "Enter a valid https website URL";
    if (!HTTPS_OK(form.maps_url)) e.maps_url = "Enter a valid https Google Maps URL";
    if (!form.consent) e.consent = "Please provide consent to submit";
    if (!form.accuracy_confirmed) e.accuracy_confirmed = "Please confirm the information is accurate";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      setTimeout(() => document.querySelector('[data-testid$="-error"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      return;
    }
    setLoading(true);
    try {
      const fixUrl = (v) => (v || "").trim().replace(/^http:\/\//i, "https://");
      await api.post("/service-providers", { ...form, website: fixUrl(form.website), maps_url: fixUrl(form.maps_url), source_url: window.location.href });
      toast.success("Registration submitted");
      navigate("/thank-you?type=provider");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHero overline={hero.overline} title={hero.title} subtitle={hero.subtitle} image={hero.image} crumbs={[{ label: "Service Provider Registration" }]} />

      {(hero.intro_html || hero.eligibility_html) && (
        <section className="bg-white" data-testid="sp-intro-section">
          <div className="mx-auto max-w-3xl px-4 pt-16 sm:px-6">
            {hero.intro_html && <div className="rich-html text-base leading-relaxed text-charcoal" dangerouslySetInnerHTML={{ __html: hero.intro_html }} />}
            {hero.eligibility_html && (
              <div className="mt-6 rounded-2xl border border-gold/25 bg-cream p-5">
                <div className="flex items-center gap-2 font-accent text-xs font-bold uppercase tracking-wide text-maroon"><ShieldCheck className="h-4 w-4" />Who can register</div>
                <div className="mt-2 rich-html text-sm leading-relaxed text-charcoal" dangerouslySetInnerHTML={{ __html: hero.eligibility_html }} />
              </div>
            )}
          </div>
        </section>
      )}

      <section className="bg-white" data-testid="service-provider-registration-section">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
          <SectionHeading overline="Registration" title={hero.form_heading || "Service Provider Details"} center />
          <form onSubmit={submit} noValidate data-testid="service-provider-form" className="mt-10 rounded-3xl border border-gold/25 bg-cream p-6 sm:p-10">
            <Field label="Provider Type" required error={errors.provider_type} testid="provider_type">
              <Select value={form.provider_type} onValueChange={(v) => set("provider_type", v)}>
                <SelectTrigger data-testid="select-provider-type"><SelectValue placeholder="Select provider type" /></SelectTrigger>
                <SelectContent>{PROVIDER_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field label="Business / Property Name" required error={errors.business_name} testid="business_name"><Input data-testid="input-business-name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} /></Field>
              <Field label="Contact Person" required error={errors.contact_person} testid="contact_person"><Input data-testid="input-contact-person" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></Field>
              <Field label="Mobile Number" required error={errors.mobile} testid="mobile"><Input data-testid="input-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+91 …" /></Field>
              <Field label="WhatsApp Number" error={errors.whatsapp} testid="whatsapp"><Input data-testid="input-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
              <Field label="Email Address" required error={errors.email} testid="email"><Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
              <Field label="City / Town" required error={errors.city} testid="city"><Input data-testid="input-city" value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
              <Field label="District" required error={errors.district} testid="district"><Input data-testid="input-district" value={form.district} onChange={(e) => set("district", e.target.value)} /></Field>
              <Field label="State" required error={errors.state} testid="state"><Input data-testid="input-state" value={form.state} onChange={(e) => set("state", e.target.value)} /></Field>
            </div>

            <div className="mt-5">
              <Field label="Services Offered / Short Description" required error={errors.services} testid="services"><Textarea data-testid="input-services" rows={3} value={form.services} onChange={(e) => set("services", e.target.value)} placeholder="Briefly describe what you offer" /></Field>
            </div>

            {/* Conditional — Accommodation */}
            {isAccommodation && (
              <div className="mt-6 rounded-2xl border border-gold/25 bg-white p-5" data-testid="sp-accommodation-fields">
                <div className="font-accent text-xs font-bold uppercase tracking-wide text-maroon">Accommodation details</div>
                <div className="mt-3 grid gap-5 sm:grid-cols-2">
                  <Field label="Property Category"><Input data-testid="input-property-category" value={form.property_category} onChange={(e) => set("property_category", e.target.value)} placeholder="e.g. 3-star, boutique, heritage" /></Field>
                  <Field label="Number of Rooms"><Input data-testid="input-num-rooms" value={form.num_rooms} onChange={(e) => set("num_rooms", e.target.value)} /></Field>
                  <Field label="Room Categories"><Input data-testid="input-room-categories" value={form.room_categories} onChange={(e) => set("room_categories", e.target.value)} placeholder="e.g. Deluxe, Suite" /></Field>
                  <Field label="Meal-Plan Availability"><Input data-testid="input-meal-plans" value={form.meal_plans} onChange={(e) => set("meal_plans", e.target.value)} placeholder="e.g. CP, MAP, AP" /></Field>
                </div>
              </div>
            )}

            {/* Conditional — Transport */}
            {isTransport && (
              <div className="mt-6 rounded-2xl border border-gold/25 bg-white p-5" data-testid="sp-transport-fields">
                <div className="font-accent text-xs font-bold uppercase tracking-wide text-maroon">Transport details</div>
                <div className="mt-3 grid gap-5 sm:grid-cols-2">
                  <Field label="Vehicle Types"><Input data-testid="input-vehicle-types" value={form.vehicle_types} onChange={(e) => set("vehicle_types", e.target.value)} placeholder="e.g. Sedan, SUV, Tempo Traveller" /></Field>
                  <Field label="Fleet Size"><Input data-testid="input-fleet-size" value={form.fleet_size} onChange={(e) => set("fleet_size", e.target.value)} /></Field>
                  <Field label="Operating Areas"><Input data-testid="input-transport-areas" value={form.transport_areas} onChange={(e) => set("transport_areas", e.target.value)} /></Field>
                </div>
              </div>
            )}

            {/* Conditional — Activity */}
            {isActivity && (
              <div className="mt-6 rounded-2xl border border-gold/25 bg-white p-5" data-testid="sp-activity-fields">
                <div className="font-accent text-xs font-bold uppercase tracking-wide text-maroon">Activity details</div>
                <div className="mt-3 grid gap-5 sm:grid-cols-2">
                  <Field label="Activity Categories"><Input data-testid="input-activity-categories" value={form.activity_categories} onChange={(e) => set("activity_categories", e.target.value)} placeholder="e.g. Trekking, Water sports" /></Field>
                  <Field label="Operating Location"><Input data-testid="input-activity-location" value={form.activity_location} onChange={(e) => set("activity_location", e.target.value)} /></Field>
                  <Field label="Capacity"><Input data-testid="input-capacity" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} placeholder="e.g. per batch" /></Field>
                  <Field label="Seasonality"><Input data-testid="input-seasonality" value={form.seasonality} onChange={(e) => set("seasonality", e.target.value)} placeholder="e.g. Oct–Mar" /></Field>
                </div>
              </div>
            )}

            {/* Optional */}
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <Field label="Website" error={errors.website} testid="website"><Input data-testid="input-website" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" /></Field>
              <Field label="Google Maps URL" error={errors.maps_url} testid="maps_url"><Input data-testid="input-maps-url" value={form.maps_url} onChange={(e) => set("maps_url", e.target.value)} placeholder="https://maps.google.com/…" /></Field>
              <Field label="Full Business Address"><Input data-testid="input-full-address" value={form.full_address} onChange={(e) => set("full_address", e.target.value)} /></Field>
              <Field label="Operating Destinations / Areas"><Input data-testid="input-operating-areas" value={form.operating_areas} onChange={(e) => set("operating_areas", e.target.value)} /></Field>
              <Field label="Years in Operation"><Input data-testid="input-years" value={form.years_in_operation} onChange={(e) => set("years_in_operation", e.target.value)} /></Field>
              <Field label="GST Number"><Input data-testid="input-gst" value={form.gst} onChange={(e) => set("gst", e.target.value)} /></Field>
              <Field label="Registration / Licence Reference"><Input data-testid="input-license" value={form.license_ref} onChange={(e) => set("license_ref", e.target.value)} /></Field>
            </div>
            <div className="mt-5">
              <Field label="Message / Notes"><Textarea data-testid="input-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} /></Field>
            </div>

            {hero.ack_html && <div className="mt-6 rounded-lg border border-gold/25 bg-white/60 p-4 rich-html text-xs leading-relaxed text-muted-foreground" data-testid="sp-ack-text" dangerouslySetInnerHTML={{ __html: hero.ack_html }} />}

            <label className="mt-6 flex items-start gap-3">
              <Checkbox data-testid="checkbox-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" />
              <span className="text-sm text-charcoal">I consent to Maha Sampada storing my details and contacting me about this expression of interest.<span className="text-saffron"> *</span></span>
            </label>
            {errors.consent && <p data-testid="consent-error" className="mt-1 text-xs text-destructive">{errors.consent}</p>}
            <label className="mt-3 flex items-start gap-3">
              <Checkbox data-testid="checkbox-accuracy" checked={form.accuracy_confirmed} onCheckedChange={(v) => set("accuracy_confirmed", !!v)} className="mt-0.5" />
              <span className="text-sm text-charcoal">I confirm the information submitted is accurate.<span className="text-saffron"> *</span></span>
            </label>
            {errors.accuracy_confirmed && <p data-testid="accuracy_confirmed-error" className="mt-1 text-xs text-destructive">{errors.accuracy_confirmed}</p>}

            <Button type="submit" data-testid="submit-service-provider" disabled={loading} className="mt-8 w-full rounded-full bg-saffron font-accent font-semibold text-white hover:bg-saffron-dark sm:w-auto sm:px-10">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting…</> : "Submit Registration"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
